<template>
  <ConfiguratorLayout>
    <template #main>
      <div class="position-relative">
        <Stepper :steps="stepper_items" :active_step="1" @addToCart="AddToCart" />
        <div class="configurator-controls">
          <Tooltip text="Toggle Sidebar">
            <v-btn class="d-block ma-auto mb-lg-2" variant="text" size="40" icon="mdi-menu" @click="ToggleDrawer" />
          </Tooltip>
          <CopierCart class="d-block mt-lg-5 ma-auto" />
          <Tooltip text="Zoom In">
            <v-btn @click="ZoomIn" size="40" class="d-block ma-auto mt-lg-2" variant="text"
              icon="mdi-magnify-plus-outline" />
          </Tooltip>
          <Tooltip text="Zoom out">
            <v-btn @click="ZoomOut" size="40" class="d-block ma-auto mt-lg-2" variant="text"
              icon="mdi-magnify-minus-outline" />
          </Tooltip>
        </div>
      </div>

      <ImageStacks :product="copier.instance" :attachments="DatasetsAttached" />

      <!--The Available Printer Accessories-->
      <DatasetsList :is_fetching="accessories_fetching" :copier="copier" v-if="copier.instance" @SelectDataset="SelectDataset"
        @UnselectDataset="UnselectDataset" />
    </template>

    <!--The added accessories on the right sidebar-->
    <template #drawer>
      <AddedAccessoryItem :title="copier.instance?.name || ''" subtitle="Main Body" v-if="copier.instance" />
      <AddedAccessoryItem :title="Accessory(item).name" :subtitle="Accessory(item).part_number"
        v-for="item in DatasetsAttached" :key="item.id" @remove="UnselectDataset(item)" :action="true" />
      <NoAccessoryAddedYet v-if="!DatasetsAttached.length" />
      <RemoveAccessoriesBtn v-if="DatasetsAttached.length" :count="DatasetsAttached.length"
        @onRemove="RemoveAllAccessories" />
    </template>

    <!--Buttons in the Drawer's Appbar-->
    <template #drawerAppbar>
      <Tooltip text="Undo last action">
        <v-btn
          :disabled="!undo_list.length"
          @click="UndoLastAccessory"
          size="32"
          class="ml-2 rounded-lg"
          variant="tonal"
          icon="mdi-undo"
        />
      </Tooltip>
      <Tooltip text="Redo last action">
        <v-btn
          :disabled="!redo_list.length"
          @click="RedoLastAccessory"
          size="32"
          class="ml-2 rounded-lg"
          variant="tonal"
          icon="mdi-redo"
        />
      </Tooltip>
      <Tooltip text="Remove all accessories">
        <v-btn
          :disabled="!DatasetsAttached.length"
          size="32"
          class="ml-2 rounded-lg"
          variant="tonal"
          icon="mdi-refresh"
          @click="RemoveAllAccessories"
        />
      </Tooltip>

      <v-spacer v-if="is_mobile" />
      <Tooltip text="Hide Sidebar" v-if="is_mobile">
        <v-btn size="small" class="ml-3 rounded-pill" variant="tonal" color="primary" @click="ToggleDrawer">Go back</v-btn>
      </Tooltip>
    </template>

    <template #drawerActions>
      <AddToQuoteBtn @onAdd="AddToCart" />
    </template>
  </ConfiguratorLayout>

  <!--Conflict Modal-->
  <Modal
    :color="ConfigModalsColor || '#f59e0b'"
    v-if="conflicts.modal"
    :visible="true"
    title="Compatibility Conflict"
    max_width="600"
    @close="CancelConflictAction">
    <div class="conflict-modal-body pa-6">
      <div class="d-flex align-start">
        <div class="conflict-alert-icon-wrapper mt-1 mr-4">
          <v-icon size="20" color="amber-darken-3">mdi-alert-decagram-outline</v-icon>
        </div>
        <div>
          <h3 class="conflict-heading text-h6 font-weight-bold mt-0">
            You're adding: <span class="added-item-name font-weight-black text-amber-darken-4">{{ conflicts.title }}</span>
            <br><small class="font-weight-light">{{ conflicts.subtext }}</small>
          </h3>
        </div>
      </div>

      <div class="conflicting-items-box pa-4 rounded-lg">
        <div class="d-flex flex-column gap-2">
          <div
            v-for="ds_item in conflicts.items"
            :key="ds_item.id"
            class="conflicting-item-row d-flex align-center py-2 px-3 rounded-lg border"
          >
            <AccessoryName :accessory="Accessory(ds_item)" />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="conflict-modal-footer d-flex w-100 pa-2 pb-0 justify-end gap-3 align-center">
        <v-btn
          class="conflict-btn conflict-btn-cancel"
          variant="flat"
          @click="CancelConflictAction"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          class="conflict-btn conflict-btn-ok"
          variant="flat"
          @click="CallConflictAction">
          Continue
        </v-btn>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import Server from "@/plugins/Server"

// Other Components
import Modal from "@/components/misc/Modal.vue"
import Stepper from "@/components/configurator/Stepper.vue"
import CopierCart from "@/components/configurator/CopierCart.vue"
import ImageStacks from "@/components/configurator/ImageStacks.vue"
import DatasetsList from "@/components/catalog/DatasetsList.vue"
import AddedAccessoryItem from "@/components/configurator/AddedAccessoryItem.vue"
import RemoveAccessoriesBtn from "@/components/configurator/RemoveAccessoriesBtn.vue"
import NoAccessoryAddedYet from "@/components/configurator/NoAccessoryAddedYet.vue"
import AddToQuoteBtn from "@/components/configurator/AddToQuoteBtn.vue"
import Tooltip from "@/components/typography/Tooltip.vue"
import AccessoryName from "@/components/configurator/AccessoryName.vue"
import ConfiguratorLayout from "@/layouts/ConfiguratorLayout.vue"

const store = useGlobalsStore()
const {
  datasets,
  is_mobile,
  cart_items,
  zoom_level,
  accessories,
  config_drawer,
  selected_copier,
  addtocart_trigger,
  ConfigModalsColor
} = storeToRefs(store)

interface ConflictState {
  modal: boolean
  items: any[]
  title: string
  subtext: string
  todos: any[]
}

const conflicts = ref<ConflictState>({
  modal: false,
  items: [],
  title: '',
  subtext: '',
  todos: []
})

const copier = ref({
  instance: null as any,
  datasets: [] as any[],
  accessories: [] as any[]
})

const undo_list = ref<any[]>([])
const redo_list = ref<any[]>([])
const stepper_items = ref<string[]>([])
const accessories_fetching = ref<boolean>(false)

// Methods
function Accessory(dataset: any) {
  return copier.value.accessories.find((acc: any) => acc.id === dataset.accessory_id) || { name: '', part_number: '' }
}

function Dataset(id: any) {
  return copier.value.datasets.find((ds: any) => ds.id == id)
}

function SelectDataset(dataset: any) {
  CancelConflictAction()
  const rules = JSON.parse(dataset.rules || '[]')
  if (rules.length < 1) {
    dataset.selected = true
    return store.SetSuccess('Accessory added')
  }

  let has_error = false
  const incompatibles: any[] = []
  const requires_all_missing: any[] = []

  rules.forEach((rule: any) => {
    if (has_error) return

    if (rule.type == 'incompatible_with') {
      rule.items.forEach((ds_id: any) => {
        const found = Dataset(ds_id)
        if (found && found.selected) {
          incompatibles.push(found)
        }
      })
    } else if (rule.type == 'require_one') {
      let passed = false
      let items_txt = "<br><br>"
      rule.items.forEach((ds: any) => {
        const found = Dataset(ds)
        if (found) {
          items_txt += '- ' + Accessory(found).name + '<br>'
          if (found.selected) passed = true
        }
      })
      if (!passed) {
        dataset.selected = false
        rule.items.forEach((ds_id: any) => {
          const dataset_elem = document.querySelector(`#dataset-id-${ds_id}`)
          if (dataset_elem) {
            dataset_elem.classList.add('blink-yellow')
            setTimeout(() => { dataset_elem.classList.remove('blink-yellow') }, 2400)
          }
        })
        CancelConflictAction()
        store.SetError(`This accessory requires one of the following. \n${items_txt}`)
        has_error = true
      }
    } else if (rule.type == 'require_all') {
      rule.items.forEach((ds: any) => {
        const found = Dataset(ds)
        if (!found || !found.selected) {
          if (found) {
            requires_all_missing.push(found)
          }
        }
      })
    }
  })

  if (has_error) return

  if (incompatibles.length > 0) {
    conflicts.value.items = incompatibles
    conflicts.value.title = Accessory(dataset).name
    conflicts.value.subtext = 'The following accessories will be removed:'
    conflicts.value.modal = true
    incompatibles.forEach(conflict => {
      conflicts.value.todos.push({
        action: 'remove',
        dataset: conflict
      })
    })
    conflicts.value.todos.push({ action: 'add', dataset: dataset })
    return
  }

  if (requires_all_missing.length > 0) {
    conflicts.value.title = Accessory(dataset).name
    conflicts.value.subtext = "The following items will be added:"
    conflicts.value.modal = true
    requires_all_missing.forEach(found => {
      conflicts.value.items.push(found)
      conflicts.value.todos.push({
        action: 'add',
        dataset: found
      })
    })
    conflicts.value.todos.push({
      action: 'add',
      dataset: dataset,
    })
    return
  }

  // All rules passed successfully
  dataset.selected = true
  if (!undo_list.value.includes(dataset.id)) undo_list.value.push(dataset.id)
  store.SetSuccess('Accessory added')
}

function CallConflictAction() {
  conflicts.value.todos.forEach(todo => {
    if (todo.action == 'remove') {
      todo.dataset.selected = false
    }
    if (todo.action == 'add') {
      todo.dataset.selected = true
      store.SetSuccess('Accessory added')
    }
  })
  CancelConflictAction()
}

function CancelConflictAction() {
  conflicts.value = {
    modal: false,
    items: [],
    title: '',
    subtext: '',
    todos: []
  }
}

function UnselectDataset(dataset: any) {
  const conflictsList = DatasetsThatRequiresThis(dataset)
  if (conflictsList.length < 1) {
    dataset.selected = false
    if (undo_list.value.includes(dataset.id)) {
      const index = undo_list.value.findIndex(id => id == dataset.id)
      undo_list.value.splice(index, 1)
    }
    if (!redo_list.value.includes(dataset.id)) {
      redo_list.value.push(dataset.id)
    }
  } else {
    conflicts.value.items = conflictsList
    conflicts.value.title = Accessory(dataset).name
    conflicts.value.subtext = 'The following accessories will be removed:'
    conflicts.value.modal = true
    conflicts.value.todos.push({ action: 'remove', dataset: dataset })
    conflictsList.forEach(conflict => {
      conflicts.value.todos.push({
        action: 'remove',
        dataset: conflict
      })
    })
  }
}

function DatasetsThatRequiresThis(dataset: any) {
  const list: any[] = []
  copier.value.datasets.forEach(ds => {
    const rules = JSON.parse(ds.rules || '[]')
    rules.forEach((rule: any) => {
      if (rule.type == 'require_one' || rule.type == 'require_all') {
        rule.items.forEach((item: any) => {
          if (item == dataset.id && ds.selected) {
            list.push(ds)
          }
        })
      }
    })
  })
  return list
}

function RemoveAllAccessories() {
  copier.value.datasets.forEach(dataset => {
    dataset.selected = false
  })
  redo_list.value = []
  undo_list.value = []
}

function AddToCart() {
  const current_items = [...cart_items.value]
  current_items.push({
    product: JSON.parse(JSON.stringify(copier.value.instance)),
    datasets: JSON.parse(JSON.stringify(copier.value.datasets)),
    accessories: JSON.parse(JSON.stringify(copier.value.accessories)),
    quantity: 1,
  })
  copier.value = { instance: null, datasets: [], accessories: [] }
  store.SetCartItems(current_items)
  store.SetSuccess("1 item(s) added to quote")

  store.FormSelector()

  store.SetState('form_modal', true)
  store.SetState('selected_copier', null)
  store.SetState('configurator', false)
}

function ToggleDrawer() {
  store.SetAnyState({
    target: 'config_drawer',
    value: !config_drawer.value
  })
}

function ZoomIn() {
  if (zoom_level.value < 2) {
    store.SetAnyState({
      target: 'zoom_level',
      value: zoom_level.value + 0.1
    })
  }
}

function ZoomOut() {
  if (zoom_level.value > 0.5) {
    store.SetAnyState({
      target: 'zoom_level',
      value: zoom_level.value - 0.1
    })
  }
}

function UndoLastAccessory() {
  const dataset_id = undo_list.value[undo_list.value.length - 1]
  if (dataset_id !== undefined) {
    redo_list.value.push(dataset_id)
    const index = copier.value.datasets.findIndex((dataset: any) => dataset.id === dataset_id)
    if (index !== -1) {
      const item = copier.value.datasets[index]
      if (item) {
        item.selected = false
      }
    }
  }
  undo_list.value.splice(-1, 1)
}

function RedoLastAccessory() {
  const dataset_id = redo_list.value[redo_list.value.length - 1]
  if (dataset_id !== undefined) {
    undo_list.value.push(dataset_id)
    const index = copier.value.datasets.findIndex((dataset: any) => dataset.id === dataset_id)
    if (index !== -1) {
      const item = copier.value.datasets[index]
      if (item) {
        item.selected = true
      }
    }
  }
  redo_list.value.splice(-1, 1)
}

function SetupStepper() {
  stepper_items.value = []
  if (copier.value.instance) {
    stepper_items.value.push("<i class='mdi mdi-printer'></i> " + copier.value.instance.model_number)
    stepper_items.value.push("<i class='mdi mdi-cog'></i> Accessories")
    stepper_items.value.push("<i class='mdi mdi-plus-circle-outline'></i> Add Product")
  }
}

const DatasetsAttached = computed(() => {
  if (!copier.value.instance) return []
  return copier.value.datasets.filter((dataset: any) => dataset.selected)
})

onMounted(() => {
  if (selected_copier.value) {
    copier.value.instance = selected_copier.value
    accessories_fetching.value = true
    Server.get(`/quotetool/fetch-dependencies/${copier.value.instance.id}`).then(res => {
      copier.value.datasets = res.data.datasets
      copier.value.accessories = res.data.accessories
    }).finally(() => {
      accessories_fetching.value = false
    });
    SetupStepper();
  }
})

onUnmounted(() => {
  store.SetSelectedCopier(null)
  copier.value = {
    instance: null,
    datasets: [],
    accessories: []
  }
})

watch(addtocart_trigger, () => {
  AddToCart()
})

watch(datasets, () => {
  if (selected_copier.value) {
    copier.value.datasets = datasets.value.filter((ds: any) => +ds.product_id == +selected_copier.value.id)
  }
})

watch(accessories, () => {
  if (selected_copier.value) {
    copier.value.datasets = datasets.value.filter((ds: any) => +ds.product_id == +selected_copier.value.id)
  }
})
</script>

<style>
.v-application__wrap {
  min-height: fit-content;
}

.blink-yellow {
  animation: blinkYellow 0.8s linear 3;
}

@keyframes blinkYellow {

  0%,
  100% {
    background-color: transparent;
  }

  50% {
    background-color: yellow;
  }
}

.configurator-controls {
  width: fit-content;
  text-align: center;
  position: absolute;
  padding: 5px;
  top: 0;
  right: 0;
  transition: .3s;
  z-index: 2;
}

/* Redesigned Conflict Modal Styling */
.conflict-modal-body {
  background-color: #ffffff !important;

  .conflict-alert-icon-wrapper {
    background-color: #fff9db !important;
    border-radius: 50% !important;
    padding: 12px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: 1px solid #ffe066 !important;
  }

  .added-item-name {
    color: #d97706 !important;
  }

  .conflicting-items-box {
    background-color: #fafafa !important;
    border: 1px solid #f3f4f6 !important;
  }

  .conflicting-item-row {
    background-color: #ffffff !important;
    border: 1px solid #e5e7eb !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    color: #374151 !important;
  }
}

.conflict-modal-footer {
  border-top: 1px solid #e5e7eb !important;
  display: flex !important;
  width: 100% !important;
  gap: 12px !important;

  .conflict-btn {
    font-family: 'Outfit', sans-serif !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    padding: 8px 20px !important;
    height: 40px !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }

  .conflict-btn-cancel {
    background-color: #ffffff !important;
    color: #4b5563 !important;
    border: 1px solid #d1d5db !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;

    &:hover {
      background-color: #f3f4f6 !important;
      color: #1f2937 !important;
    }
  }

  .conflict-btn-ok {
    background: #2a73c5;
    color: #ffffff !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;

    &:hover {
      box-shadow: 0 4px 6px -1px rgba(217, 119, 6, 0.2) !important;
    }
  }
}
</style>
